import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { useAppState } from "../context/StateContext";
import { Customer, CustomerIds } from "../models/customer";
import CustomerService from "../services/customer.service";
import { Status } from "../util/enums";
import { CustomerKeys } from "../util/queries";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

const useActionCustomers = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  interface GetAllCustomersRequestProps {
    companyId?: number;
  }

  const getAllCustomers = ({ companyId }: GetAllCustomersRequestProps) => {
    return useQuery({
      queryKey: CustomerKeys.getAll([companyId]),
      queryFn: async () => {
        const response = await CustomerService.getAll({
          companyId,
          status: Status.ACTIVE,
          sortBy: JSON.stringify([["id", "ASC"]]),
        });

        if (response && response.data) return response.data;

        return [];
      },

      enabled: !!currentUser?.companyId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const useInviteCustomers = (
    customerIds: CustomerIds,
    companyId: number,
    callback?: (customerIds?: CustomerIds) => void
  ) => {
    return useQueries({
      queries: customerIds.map((customerId) => ({
        queryKey: CustomerKeys.invite(customerId),
        queryFn: () => CustomerService.inviteToShipeezi({ customerId, companyId }),
        enabled: false,
        refetchOnWindowFocus: false,
        retry: false,
        onSuccess: () => {
          NotificationToast.success(translate("inviteSentSuccessfully"), {
            id: "invite-customer-success",
          });
          if (callback) callback(customerIds);
        },
      })),
    });
  };

  const getCustomersById = (
    customerIds: CustomerIds,
    setCustomers: React.Dispatch<React.SetStateAction<Customer[]>>
  ) => {
    return useQuery({
      queryKey: CustomerKeys.getAll([customerIds]),
      queryFn: () =>
        CustomerService.getAll({
          companyId: currentUser?.companyId,
          id: customerIds,
          attributes: JSON.stringify(["id", "name", "email", "companyId", "associatedCompanyId"]),
        }),
      enabled: customerIds.length > 0,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setCustomers(response.data);
        }
      },
    });
  };

  return {
    getAllCustomers,
    useInviteCustomers,
    getCustomersById,
  };
};

export default useActionCustomers;
